import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Chip,
  Checkbox,
  Modal,
  TextField,
  Typography,
  FormControlLabel,
  Tooltip,
  IconButton,
  Alert,
  Fade,
  Autocomplete,
} from '@mui/material';
import { Stack } from '@mui/system';
import { TOSButton } from './style';
import { ColorsV2 } from 'app/constants/color';
import apiService from 'app/services/apiService';
import { isValidEmail } from 'app/utils';

const TOSInvitationModal = ({
  open,
  handleClose,
  recommendedEmails,
  setRecommendedEmails,
  tosBatch,
  clearSelectedQuestions,
  setFrameworkData,
  setToast,
}) => {
  const [emailChecked, setEmailChecked] = useState(true);
  const [codeRequired, setcodeRequired] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [codeTOS, setCodeTOS] = useState([]);
  const [copiedAlerts, setCopiedAlerts] = useState([]);

  const [email, setEmail] = useState('');
  const [selectedEmails, setSelectedEmails] = useState([]);
  // const [emailError, setEmailError] = useState(false);
  // const [emailHelperText, setEmailHelperText] = useState('');

  const [message, setMessage] = useState('');

  const [submitError, setSubmitError] = useState(false);

  const { t } = useTranslation('translation', { keyPrefix: 'gap_analysis' });

  const createTOSUrl = async () => {
    let res = null;
    try {
        const users = {};
        selectedEmails.forEach(email => {
            users[email] = {
                fname: '',
                lname: '',
                sendmail: emailChecked,
                questions: tosBatch,
            };
        });

        res = await apiService.createTOSUrl({ users });

        if (res && res.data) {
          const codes = [];
          const urls = [];
          selectedEmails.forEach(email => {
              if (res.data.endpoints[email]) {
                  codes.push({ code: res.data.endpoints[email].code, email });
                  urls.push(res.data.endpoints[email].path);
              }
          });
          setcodeRequired(res.data.codeRequired);
          if (res.data.codeRequired) {
              setCodeTOS(codes);
          }

          setFrameworkData(prevState => {
              const newState = JSON.parse(JSON.stringify(prevState)); // Deep copy

              const updateNestedKey = (obj, id, recipients) => {
                  for (const key in obj) {
                      if (Object.prototype.hasOwnProperty.call(obj, key)) {
                          if (key === id) {
                              obj[key].tosRecipients = recipients;
                          } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                              updateNestedKey(obj[key], id, recipients);
                          }
                      }
                  }
                  return obj;
              };

              Object.entries(res.data?.recipients || {}).forEach(([question, recipients]) => {
                  updateNestedKey(newState, question, recipients || []);
              });
              return newState;
          });
          clearSelectedQuestions();
      }
    } catch (error) {
        setToast({ open: true, severity: 'error', message: error?.response?.data?.hint || 'An error occurred' });
        console.error(error);
        handleClose();
    }
};


  const handleEmailChange = (event, newEmailValue) => {
    setEmail(newEmailValue);
  };

  const handleSelectedEmailChange = (event, newValue) => {
    const lastValue = newValue[newValue.length - 1];
    if (email && !recommendedEmails.includes(lastValue)) {
      setRecommendedEmails(prevEmails => [...prevEmails, email]);
    }
    setSelectedEmails(newValue);
  };

  const filterOptions = options => {
    const filtered = options.filter(option => option.toLowerCase().includes(email.toLowerCase()));
    // If the inputValue is not in the filtered options, add it as the last option
    if (email !== '' && !options.includes(email)) {
      if (isValidEmail(email)) {
        filtered.push(email);
      } else {
        filtered.push(t('tos_valid_email_prompt'));
      }
    }
    return filtered;
  };

  const handleSendEmailChange = event => {
    setEmailChecked(event.target.checked);
  };

  const handleMessageChange = event => {
    setMessage(event.target.value);
  };

  const handleSubmit = () => {
    if (!selectedEmails) {
      setSubmitError(true);
      setTimeout(() => {
        setSubmitError(false);
      }, 3000);
      return;
    }
    setSubmitted(true);
    createTOSUrl();
  };

  const handleCopy = (index, code) => {
    navigator.clipboard.writeText(code);
    setCopiedAlerts(prevCopiedAlerts => {
      const newCopiedAlerts = [...prevCopiedAlerts];
      newCopiedAlerts[index] = true;
      return newCopiedAlerts;
    });

    setTimeout(() => {
      // Add cleanup for this timeout
      setCopiedAlerts(prevCopiedAlerts => {
        const newCopiedAlerts = [...prevCopiedAlerts];
        newCopiedAlerts[index] = false;
        return newCopiedAlerts;
      });
    }, 3000);
  };

  useEffect(() => {
    setcodeRequired(true);
    setEmailChecked(true);
    setSubmitted(false);
  }, [open]);

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        sx={{
          color: ColorsV2.white,
        }}
      >
        <Stack
          direction='column'
          sx={{
            width: 600,
            backgroundColor: ColorsV2.secondary,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            padding: '20px',
            borderRadius: '4px',
          }}
        >
          <Typography
            sx={{
              color: ColorsV2.textInfo,
            }}
          >
            {submitted
              ? codeRequired
                ? // If submitted and a code is required display code description
                  t('tos_description_code')
                : // If submitted and no code is required show success status
                  t('tos_description_no_code')
              : // If not yet submitted
                t('tos_description_no_submit')}
          </Typography>

          {/* <Typography>
              {urlTOS}
            </Typography> */}

          {/* Handling of no email sent and error on url generation still needs to be added */}
          {submitted ? (
            codeRequired ? (
              // If submitted and a code is required display code UI
              <Stack direction='column' justifyContent='center' alignItems='center'>
                {codeTOS.length < 1 ? (
                  <Typography>{t('tos_loading_code')}</Typography>
                ) : (
                  codeTOS.map((user, index) => {
                    return (
                      <Stack
                        direction='column'
                        alignItems='flex-start'
                        key={index}
                        sx={{
                          width: '100%',
                          borderRadius: '4px',
                          backgroundColor: ColorsV2.primary,
                          padding: '5px',
                          marginTop: '10px',
                          marginBottom: '10px',
                        }}
                      >
                        <Typography
                          variant='subtitle1'
                          sx={{
                            alignSelf: 'flex-start',
                            marginLeft: '10px',
                            color: ColorsV2.white,
                          }}
                        >
                          {user.email}
                        </Typography>
                        <Stack direction='row' justifyContent='center' alignItems='center'>
                          {user.code
                            .toString()
                            .split('')
                            .map((digit, index) => {
                              return (
                                <Typography
                                  key={index}
                                  variant='h2'
                                  sx={{
                                    border: 'solid 3px' + ColorsV2.secondary,
                                    padding: '10px',
                                    margin: '10px',
                                    color: ColorsV2.textInfo,
                                  }}
                                >
                                  {digit}
                                </Typography>
                              );
                            })}
                          <Stack direction='row' alignItems='center'>
                            <Tooltip title={t('tos_code_clipboard_tooltip')}>
                              <IconButton
                                onClick={() => handleCopy(index, user.code)}
                                sx={{
                                  color: ColorsV2.acent,
                                  '&:hover': {
                                    backgroundColor: ColorsV2.acent,
                                    color: ColorsV2.white,
                                  },
                                }}
                              >
                                <ContentCopyIcon></ContentCopyIcon>
                              </IconButton>
                            </Tooltip>
                            <Fade in={copiedAlerts[index]}>
                              <Alert
                                sx={{
                                  backgroundColor: ColorsV2.secondary,
                                  color: ColorsV2.white,
                                }}
                              >
                                {t('clipboard_alert')}
                              </Alert>
                            </Fade>
                          </Stack>
                        </Stack>
                      </Stack>
                    );
                  })
                )}
                <TOSButton
                  variant='text'
                  size='large'
                  sx={{
                    maxWidth: { lg: '180px', xl: '280px' },
                    alignSelf: 'flex-end',
                    backgroundColor: ColorsV2.primary,
                    border: '2px solid ' + ColorsV2.outline,
                    color: ColorsV2.white,

                    '&:hover': {
                      backgroundColor: ColorsV2.primary,
                      border: '2px solid ' + ColorsV2.acent,
                      color: ColorsV2.acent,
                    },
                  }}
                  onClick={handleClose}
                >
                  {t('close')}
                </TOSButton>
              </Stack>
            ) : (
              // If submitted and no code is required
              <Typography
                sx={{
                  marginTop: '20px',
                  marginBottom: '20px',
                }}
              >
                {t('tos_no_code_title')}
              </Typography>
            )
          ) : (
            // If not yet submitted display the form UI
            <Stack>
              <Autocomplete
                freesolo='true'
                options={recommendedEmails}
                multiple
                value={selectedEmails} // The selected values
                onChange={handleSelectedEmailChange} // Handle new selections, including custom input
                inputValue={email} // Control the input field
                onInputChange={handleEmailChange} // Handle changes to the input field
                getOptionDisabled={option => option === 'Please enter a valid email'}
                filterOptions={filterOptions}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      key={option}
                      label={option}
                      deleteIcon={<ClearIcon />}
                      {...getTagProps({ index })}
                      sx={{
                        color: ColorsV2.white,
                        backgroundColor: ColorsV2.primary,
                        '& .MuiChip-deleteIcon': {
                          color: ColorsV2.secondary,
                          '&:hover': {
                            color: ColorsV2.acent,
                          },
                        },
                      }}
                    />
                  ))
                }
                renderInput={params => (
                  <TextField
                    variant='outlined'
                    label='E-mail'
                    // error={emailError}
                    // helperText={emailHelperText}
                    required
                    {...params}
                    sx={{
                      marginTop: '20px',
                      marginBottom: '20px',
                      // Root class for the input field
                      '& .MuiOutlinedInput-root': {
                        color: ColorsV2.white,
                        // Class for the border around the input field
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: ColorsV2.primary,
                          borderWidth: '2px',
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'secondary.main',
                            borderWidth: '3px',
                          },
                        },
                      },
                      // Class for the label of the input field
                      '& .MuiInputLabel-outlined': {
                        color: ColorsV2.white,
                        fontWeight: 'bold',
                        '&.Mui-focused': {
                          color: ColorsV2.acent,
                        },
                      },
                      '& .MuiAutocomplete-clearIndicator': {
                        visibility: 'visible',
                        color: ColorsV2.primary,
                        '&:hover': {
                          color: ColorsV2.acent,
                        },
                      },
                    }}
                  />
                )}
              ></Autocomplete>
              <Tooltip title='Include a short message'>
                <TextField
                  label='Message'
                  multiline
                  maxRows={4}
                  value={message}
                  onChange={handleMessageChange}
                  sx={{
                    display: 'none',
                    marginTop: '20px',
                    marginBottom: '20px',
                    // Root class for the input field
                    '& .MuiOutlinedInput-root': {
                      color: ColorsV2.white,
                      // Class for the border around the input field
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: ColorsV2.primary,
                        borderWidth: '2px',
                      },
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'secondary.main',
                          borderWidth: '3px',
                        },
                      },
                    },
                    // Class for the label of the input field
                    '& .MuiInputLabel-outlined': {
                      color: ColorsV2.white,
                      fontWeight: 'bold',
                      '&.Mui-focused': {
                        color: ColorsV2.acent,
                      },
                    },
                  }}
                ></TextField>
              </Tooltip>
              <FormControlLabel
                label={t('tos_automatic_email')}
                sx={{
                  display: 'none',
                }}
                control={
                  <Checkbox
                    checked={emailChecked}
                    onChange={handleSendEmailChange}
                    style={{ justifySelf: 'right' }}
                    sx={{
                      color: ColorsV2.acent,
                      '&.Mui-checked': {
                        color: ColorsV2.white,
                      },

                      '&:hover': {
                        color: ColorsV2.white,
                        bgcolor: ColorsV2.acent,
                      },
                    }}
                  ></Checkbox>
                }
              ></FormControlLabel>
              <Typography
                sx={{
                  marginTop: '20px',
                  marginBottom: '20px',
                }}
              >
                {emailChecked
                  ? t('tos_auto_email_description')
                  : t('tos_no_auto_email_description')}
              </Typography>
              <Stack
                direction='row'
                sx={{
                  gap: '10px',
                  alignSelf: 'flex-end',
                }}
              >
                <TOSButton
                  variant='text'
                  size='large'
                  sx={{
                    maxWidth: { lg: '180px', xl: '280px' },
                    alignSelf: 'flex-end',
                    backgroundColor: ColorsV2.primary,
                    border: '2px solid ' + ColorsV2.outline,
                    color: ColorsV2.white,

                    '&:hover': {
                      backgroundColor: ColorsV2.primary,
                      border: '2px solid ' + ColorsV2.acent,
                      color: ColorsV2.acent,
                    },
                  }}
                  onClick={handleClose}
                >
                  {t('cancel')}
                </TOSButton>
                <TOSButton
                  variant='text'
                  size='large'
                  disabled={selectedEmails?.length > 0 ? false : true}
                  sx={{
                    // maxWidth: {lg: '180px', xl: '280px'},
                    alignSelf: 'flex-end',
                    backgroundColor: ColorsV2.acent,
                    border:
                      selectedEmails?.length > 0
                        ? '2px solid ' + ColorsV2.acent
                        : '2px solid ' + ColorsV2.outline,
                    color: ColorsV2.white,

                    '&:hover': {
                      backgroundColor: ColorsV2.primary,
                      color: ColorsV2.acent,
                    },
                  }}
                  onClick={handleSubmit}
                >
                  {t('tos_generate_url')}
                </TOSButton>
              </Stack>
              <Fade in={submitError}>
                <Alert
                  severity='warning'
                  sx={{
                    backgroundColor: ColorsV2.secondary,
                    color: ColorsV2.white,
                  }}
                >
                  {t('tos_invalid_input_alert')}
                </Alert>
              </Fade>
            </Stack>
          )}
        </Stack>
      </Modal>
    </>
  );
};

export default TOSInvitationModal;
